import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { buildApiUrl, httpClient } from "shared/api/httpClient"
import type {
  AuthApiUser,
  AuthLoginResponse,
  AuthUser,
  SignInInput,
} from "modules/auth/types"
import {
  invalidateAuthSession,
  persistAuthToken,
  readStoredAuthToken,
  registerAuthSessionInvalidationListener,
  shouldHandleAuthSyncEvent,
} from "modules/auth/session"
import {
  extractApiErrorMessage,
  getAuthHeaders,
  invalidateSessionOnAuthError,
  isNetworkOrTimeoutRequestError,
} from "shared/api/serviceSupport"

const LOGIN_GENERIC_ERROR_MESSAGE =
  "Não foi possível iniciar a sessão. Verifique suas credenciais e tente novamente."
const LOGIN_FORBIDDEN_ERROR_MESSAGE =
  "Acesso não autorizado. Se o problema persistir, entre em contato com o suporte."
const PORTAL_ROLE_ONLY_LOGIN_MESSAGE =
  "No momento, o acesso autenticado está disponível apenas para emissores e devedores."
const PENDING_COMPLIANCE_ERROR_MESSAGE =
  "Seu cadastro está em análise. Avisaremos por e-mail quando for aprovado."
const PENDING_COMPLIANCE_STATUS = "pendente_compliance"

function isPendingComplianceResponse(responseBody: string) {
  const normalizedBody = responseBody.trim()

  if (!normalizedBody) {
    return false
  }

  if (
    /(pendente[_\s-]?compliance|pendente\s+de\s+compliance)/i.test(
      normalizedBody,
    )
  ) {
    return true
  }

  try {
    const parsedBody = JSON.parse(normalizedBody) as {
      status?: unknown
      code?: unknown
      error_code?: unknown
      user?: {
        status?: unknown
      }
    }

    return (
      parsedBody.status === PENDING_COMPLIANCE_STATUS ||
      parsedBody.code === PENDING_COMPLIANCE_STATUS ||
      parsedBody.error_code === PENDING_COMPLIANCE_STATUS ||
      parsedBody.user?.status === PENDING_COMPLIANCE_STATUS
    )
  } catch {
    return false
  }
}

function isPendingComplianceUser(user: AuthUser | null) {
  return user?.status === PENDING_COMPLIANCE_STATUS
}

function isPortalUser(user: AuthUser | null) {
  return user?.role === "emissor" || user?.role === "devedor"
}

function isUserRole(value: unknown): value is AuthUser["role"] {
  return value === "emissor" || value === "devedor" || value === "investidor"
}

function isUserAccountStatus(value: unknown): value is AuthUser["status"] {
  return value === "ativo" || value === PENDING_COMPLIANCE_STATUS
}

function isAuthApiUser(value: unknown): value is AuthApiUser {
  if (!value || typeof value !== "object") {
    return false
  }

  const candidate = value as Partial<AuthApiUser>

  if (
    typeof candidate.name !== "string" ||
    typeof candidate.email !== "string" ||
    !isUserRole(candidate.role)
  ) {
    return false
  }

  if (
    candidate.status !== undefined &&
    !isUserAccountStatus(candidate.status)
  ) {
    return false
  }

  if (candidate.id !== undefined && typeof candidate.id !== "string") {
    return false
  }

  if (
    candidate.avatarUrl !== undefined &&
    candidate.avatarUrl !== null &&
    typeof candidate.avatarUrl !== "string"
  ) {
    return false
  }

  if (
    candidate.assetIds !== undefined &&
    (!Array.isArray(candidate.assetIds) ||
      candidate.assetIds.some((item) => typeof item !== "string"))
  ) {
    return false
  }

  return true
}

function isAuthLoginResponse(value: unknown): value is AuthLoginResponse {
  if (!value || typeof value !== "object") {
    return false
  }

  const candidate = value as Partial<AuthLoginResponse>

  return (
    typeof candidate.access_token === "string" && isAuthApiUser(candidate.user)
  )
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isRestoring: boolean
  signIn: (input: SignInInput) => Promise<AuthUser>
  signOut: () => void
  updateUser: (patch: Partial<AuthUser>) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function mapAuthUser(data: AuthApiUser): AuthUser {
  return {
    id: data.id,
    name: data.name,
    email: data.email,
    role: data.role,
    status: data.status ?? "ativo",
    assetIds: data.assetIds,
    avatarUrl: data.avatarUrl ?? null,
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isRestoring, setIsRestoring] = useState(() =>
    Boolean(readStoredAuthToken()),
  )

  useEffect(() => {
    const unregisterInvalidationListener =
      registerAuthSessionInvalidationListener(() => {
        setUser(null)
        setIsRestoring(false)
      })

    const handleStorage = (event: StorageEvent) => {
      if (shouldHandleAuthSyncEvent(event)) {
        invalidateAuthSession({ broadcast: false })
      }
    }

    window.addEventListener("storage", handleStorage)

    return () => {
      unregisterInvalidationListener()
      window.removeEventListener("storage", handleStorage)
    }
  }, [])

  useEffect(() => {
    const token = readStoredAuthToken()

    if (!token) {
      setIsRestoring(false)
      return
    }

    let isMounted = true

    httpClient({
      url: buildApiUrl("/api/site/auth/me"),
      headers: getAuthHeaders({ token }),
    })
      .then((res) => {
        if (!isMounted) {
          return
        }

        if (!res.ok) {
          if (!invalidateSessionOnAuthError(res.status)) {
            invalidateAuthSession()
          }

          return
        }

        const data = JSON.parse(res.bodyText) as unknown

        if (!isAuthApiUser(data)) {
          invalidateAuthSession()
          return
        }

        const restoredUser = mapAuthUser(data)

        if (!isPortalUser(restoredUser) || isPendingComplianceUser(restoredUser)) {
          invalidateAuthSession()
          return
        }

        setUser(restoredUser)
      })
      .catch(() => {
        if (!isMounted) {
          return
        }

        invalidateAuthSession()
      })
      .finally(() => {
        if (isMounted) {
          setIsRestoring(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const signIn = useCallback(async (input: SignInInput): Promise<AuthUser> => {
    const normalizedEmail = input.email.trim()
    const normalizedPassword = input.password.trim()

    if (!normalizedEmail || !normalizedPassword) {
      throw new Error(
        "Preencha e-mail e senha.",
      )
    }

    try {
      const res = await httpClient({
        url: buildApiUrl("/api/site/auth/login"),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: normalizedEmail,
          password: normalizedPassword,
        }),
      })

      if (!res.ok) {
        const apiErrorMessage = extractApiErrorMessage(res.bodyText)

        // Pendencia de compliance pode chegar com status variado; checa antes
        // de classificar por codigo.
        if (
          isPendingComplianceResponse(res.bodyText) ||
          isPendingComplianceResponse(apiErrorMessage)
        ) {
          throw new Error(PENDING_COMPLIANCE_ERROR_MESSAGE)
        }

        if (res.status === 403) {
          throw new Error(LOGIN_FORBIDDEN_ERROR_MESSAGE)
        }

        // 400/401/422 e qualquer outro codigo: mensagem generica, sem expor o
        // corpo cru do backend.
        throw new Error(LOGIN_GENERIC_ERROR_MESSAGE)
      }

      const data = JSON.parse(res.bodyText) as unknown

      if (!isAuthLoginResponse(data)) {
        throw new Error(LOGIN_GENERIC_ERROR_MESSAGE)
      }

      const nextUser = mapAuthUser(data.user)

      if (!isPortalUser(nextUser)) {
        setUser(null)
        throw new Error(PORTAL_ROLE_ONLY_LOGIN_MESSAGE)
      }

      if (isPendingComplianceUser(nextUser)) {
        setUser(null)
        throw new Error(PENDING_COMPLIANCE_ERROR_MESSAGE)
      }

      persistAuthToken(data.access_token, Boolean(input.rememberMe))
      setUser(nextUser)

      return nextUser
    } catch (signInError) {
      if (isNetworkOrTimeoutRequestError(signInError)) {
        throw new Error(LOGIN_GENERIC_ERROR_MESSAGE)
      }

      if (signInError instanceof SyntaxError) {
        throw new Error(LOGIN_GENERIC_ERROR_MESSAGE)
      }

      if (signInError instanceof Error) {
        throw signInError
      }

      throw new Error(LOGIN_GENERIC_ERROR_MESSAGE)
    }
  }, [])

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((current) => (current ? { ...current, ...patch } : current))
  }, [])

  const signOut = useCallback(() => {
    const token = readStoredAuthToken()

    if (token) {
      void httpClient({
        url: buildApiUrl("/api/site/auth/logout"),
        method: "POST",
        headers: getAuthHeaders({ token }),
      })
        .then((res) => {
          if (!res.ok) {
            invalidateSessionOnAuthError(res.status)
          }
        })
        .catch(() => {})
    }

    invalidateAuthSession()
  }, [])

  const contextValue: AuthContextValue = {
    user,
    isAuthenticated: Boolean(user),
    isRestoring,
    signIn,
    signOut,
    updateUser,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider.")
  }

  return context
}
